import {format, isAfter, isBefore, isValid, parse} from 'date-fns';
import {Context, EMPTY_CONTEXT, Options, Schema} from './schema';

export const DEFAULT_DATE_FORMAT: string = 'YYYY-MM-DD';

export interface DateStringOptions extends Options<string | null> {
  dateFormat?: string;
  minDate?: string | ((context?: Context) => string | null);
  maxDate?: string | ((context?: Context) => string | null);
}

export class DateStringSchema extends Schema<string | null, DateStringOptions> {
  // Utilitary function to convert a date string to a `Date` given a date format
  public static toDate(
    dateString: string | null,
    dateFormat: string = DEFAULT_DATE_FORMAT,
  ): Date | null {
    if (dateString === null) {
      return null;
    }
    // We only return dates when they are valid, otherwise we return `null`; the
    // date constructor also accepts "invalid" dates, e.g. `'2000-02-30'`
    // becomes `'2000-03-01'`; we also return `null` in such cases
    const date = parse(dateString, dateFormat, new Date());
    return isValid(date) &&
      dateString === DateStringSchema.toDateString(date, dateFormat)
      ? date
      : null;
  }

  // Utilitary function to convert a `Date` to a date string given a date format
  public static toDateString(
    date: Date | null,
    dateFormat: string = DEFAULT_DATE_FORMAT,
  ): string | null {
    return date === null || !isValid(date) ? null : format(date, dateFormat);
  }

  // Utilitary function to check whether a given string date is valid
  public static dateStringIsValid(
    dateString: string | null,
    dateFormat: string = DEFAULT_DATE_FORMAT,
  ) {
    return (
      dateString === null ||
      DateStringSchema.toDate(dateString, dateFormat) !== null
    );
  }

  constructor(options: DateStringOptions = {}) {
    super(options);
  }

  public toJSON(): object {
    return {
      type: 'DateStringSchema',
      ...super.toJSON(),
      minDate: this._options.minDate,
      maxDate: this._options.maxDate,
    };
  }

  // Utilitary function to convert a date string to a `Date` using the schema's
  // date format
  public toDate(dateString: string | null): Date | null {
    return DateStringSchema.toDate(dateString, this.dateFormat);
  }

  // Utilitary function to convert a `Date` to a date string using the schema's
  // date format
  public toDateString(date: Date | null): string | null {
    return DateStringSchema.toDateString(date, this.dateFormat);
  }

  public get dateFormat(): string {
    return this._options.dateFormat === undefined
      ? DEFAULT_DATE_FORMAT
      : this._options.dateFormat;
  }

  public minDate(context: Context = EMPTY_CONTEXT): string | null {
    return this._options.minDate === undefined
      ? null
      : typeof this._options.minDate === 'function'
        ? this._options.minDate(context)
        : this._options.minDate;
  }

  public maxDate(context: Context = EMPTY_CONTEXT): string | null {
    return this._options.maxDate === undefined
      ? null
      : typeof this._options.maxDate === 'function'
        ? this._options.maxDate(context)
        : this._options.maxDate;
  }

  public valueToMobx(value: string | null): string | null {
    return this._nullifyValue(value);
  }

  public valueToJS(
    value: string | null,
    context: Context = EMPTY_CONTEXT,
  ): string | null {
    return this.isComputed
      ? this.computedValue(context)
      : this._nullifyValue(value);
  }

  public typeIsValid(value: any): value is string | null {
    return (
      value === null ||
      (typeof value === 'string' && this.dateStringIsValid(value))
    );
  }

  public dateStringIsValid(value: string | null): boolean {
    return DateStringSchema.dateStringIsValid(value, this.dateFormat);
  }

  public valueIsValid(
    value: string | null,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    return (
      this.requiredIsValid(value, context) &&
      this.minDateIsValid(value, context) &&
      this.maxDateIsValid(value, context) &&
      this.customFunctionIsValid(value, context)
    );
  }

  public minDateIsValid(
    value: string | null,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    const date = this.toDate(value);
    const minDate = this.toDate(this.minDate(context));
    return date === null || minDate === null || !isBefore(date, minDate);
  }

  public maxDateIsValid(
    value: string | null,
    context: Context = EMPTY_CONTEXT,
  ): boolean {
    const date = this.toDate(value);
    const maxDate = this.toDate(this.maxDate(context));
    return date === null || maxDate === null || !isAfter(date, maxDate);
  }
}
